import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Search, X, ExternalLink, TrendingUp, AlertTriangle, CheckCircle2, Clock, Wallet, CalendarClock } from "lucide-react";
import { useNavigate } from "react-router-dom";

const STATUS_COLORS = {
  paid:    "text-green-300 bg-green-500/10 border-green-500/40",
  pending: "text-yellow-300 bg-yellow-500/10 border-yellow-500/40",
  failed:  "text-red-300 bg-red-500/10 border-red-500/40",
};

const KIND_LABELS = {
  invoice: "Invoice", tournament_team: "Tournament", donation: "Donation",
  wallet_topup: "Wallet", membership: "Membership", booking: "Desk Booking",
};

export default function AdminPhonePeReport() {
  const [tab, setTab] = useState("orders");
  const [summary, setSummary] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => { loadSummary(); }, [days]);
  const loadSummary = async () => {
    try {
      const { data } = await api.get(`/admin/phonepe/report/summary?days=${days}`);
      setSummary(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-phonepe-report">
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// System · PhonePe Reconciliation</div>
      <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none mb-6">
        PhonePe Report.
      </h1>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="pp-report-summary">
        <SummaryTile icon={<CheckCircle2 size={18} />} label="Paid" value={summary?.total_paid_count ?? "—"} sub={summary && `₹${summary.total_paid_amount.toLocaleString("en-IN")}`} color="green" />
        <SummaryTile icon={<Clock size={18} />} label="Pending" value={summary?.pending_count ?? "—"} color="yellow" />
        <SummaryTile icon={<AlertTriangle size={18} />} label="Failed" value={summary?.failed_count ?? "—"} color="red" />
        <SummaryTile icon={<Wallet size={18} />} label="Refunds" value={summary?.refunds_count ?? "—"} sub={summary && `₹${summary.refunds_amount.toLocaleString("en-IN")}`} color="blue" />
        <SummaryTile icon={<TrendingUp size={18} />} label="Webhooks" value={summary?.webhook_count ?? "—"} sub={summary?.unmatched_webhook_count ? `${summary.unmatched_webhook_count} unmatched` : "all matched"} color="orange" />
      </div>

      {/* Period + tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex gap-1 border-b border-white/10">
          {[
            { id: "orders", label: "Orders" },
            { id: "refunds", label: "Refunds" },
            { id: "webhooks", label: "Webhook Events" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`pp-report-tab-${t.id}`}
              className={`px-5 py-3 font-mono text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${
                tab === t.id ? "border-brand-orange brand-orange" : "border-transparent text-white/50 hover:text-white/80"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/50">
          Period ·
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)}
                    data-testid={`pp-report-days-${d}`}
                    className={`px-2 py-1 border ${days === d ? "border-brand-orange brand-orange" : "border-white/15 text-white/40 hover:text-white/70"}`}>
              {d}d
            </button>
          ))}
          <button onClick={loadSummary} className="text-white/60 hover:text-white ml-1" title="Refresh"><RefreshCw size={14} /></button>
        </div>
      </div>

      {tab === "orders"   && <OrdersTable />}
      {tab === "refunds"  && <RefundsTable />}
      {tab === "webhooks" && <WebhooksTable />}
    </div>
  );
}

// ---------------- Orders ----------------
function OrdersTable() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ status: "", kind: "", q: "" });
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: "25" });
      if (filters.status) params.set("status", filters.status);
      if (filters.kind)   params.set("kind",   filters.kind);
      if (filters.q)      params.set("q",      filters.q);
      const { data } = await api.get(`/admin/phonepe/report/orders?${params.toString()}`);
      setRows(data.rows); setTotal(data.total); setPages(data.pages);
    } catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [page, filters.status, filters.kind]);

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3" data-testid="pp-report-orders-filters">
        <SearchBox value={filters.q} onSearch={(q) => { setPage(1); setFilters({ ...filters, q }); load(); }} placeholder="Order ID · payer · description" />
        <FilterSelect value={filters.status} onChange={(v) => { setPage(1); setFilters({ ...filters, status: v }); }}
                      options={[["", "All statuses"], ["paid", "Paid"], ["pending", "Pending"], ["failed", "Failed"]]} testid="pp-filter-status" />
        <FilterSelect value={filters.kind} onChange={(v) => { setPage(1); setFilters({ ...filters, kind: v }); }}
                      options={[["", "All kinds"], ["booking", "Desk Booking"], ["invoice", "Invoice"], ["membership", "Membership"], ["wallet_topup", "Wallet"], ["tournament_team", "Tournament"], ["donation", "Donation"]]} testid="pp-filter-kind" />
      </div>

      <div className="border border-white/10 overflow-x-auto" data-testid="pp-report-orders-table">
        <table className="w-full text-xs">
          <thead className="text-white/50 font-mono uppercase tracking-widest text-[10px] border-b border-white/10">
            <tr>
              <Th>When</Th><Th>Order ID</Th><Th>Kind</Th><Th>Amount</Th><Th>Status</Th><Th>Payer</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><Td colSpan={7} className="text-center text-white/50 py-8">Loading…</Td></tr>}
            {!loading && rows.length === 0 && <tr><Td colSpan={7} className="text-center text-white/40 py-8">No PhonePe orders yet.</Td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-white/5 hover:bg-white/2">
                <Td className="text-white/60 whitespace-nowrap">{fmtDate(r.created_at)}</Td>
                <Td className="font-mono text-[11px] break-all">{r.phonepe_merchant_order_id || "—"}</Td>
                <Td><span className="text-[10px] font-mono uppercase text-white/60">{KIND_LABELS[r.kind] || r.kind}</span></Td>
                <Td className="whitespace-nowrap">₹{Number(r.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</Td>
                <Td><StatusBadge s={r.payment_status} /></Td>
                <Td className="whitespace-nowrap">{r.payer_name || <span className="text-white/40">—</span>}</Td>
                <Td>
                  <button onClick={() => setDetail(r.id)} className="text-brand-blue hover:underline font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-1" data-testid={`pp-open-order-${r.id}`}>
                    View <ExternalLink size={11} />
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pager page={page} pages={pages} total={total} onPage={setPage} />
      {detail && <OrderDetailDrawer txnId={detail} onClose={() => setDetail(null)} onReconciled={load} />}
    </div>
  );
}

function OrderDetailDrawer({ txnId, onClose, onReconciled }) {
  const [data, setData] = useState(null);
  const [replaying, setReplaying] = useState(false);
  const navigate = useNavigate();
  useEffect(() => { (async () => {
    try {
      const r = await api.get(`/admin/phonepe/report/orders/${txnId}`);
      setData(r.data);
    } catch (e) { toast.error(apiError(e)); onClose(); }
  })(); }, [txnId]);

  const replay = async () => {
    setReplaying(true);
    try {
      const r = await api.post("/admin/phonepe/report/replay", { txn_id: txnId });
      toast.success(`Replayed → ${r.data.payment_status}`);
      const d = await api.get(`/admin/phonepe/report/orders/${txnId}`);
      setData(d.data);
      onReconciled?.();
    } catch (e) { toast.error(apiError(e)); }
    setReplaying(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="pp-order-drawer">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[#0F1524] border-l border-white/12 overflow-y-auto p-8">
        <button onClick={onClose} data-testid="pp-drawer-close" className="absolute top-4 right-4 text-white/60 hover:text-white"><X size={22} /></button>
        <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-2">// Order Detail</div>
        {!data ? <div className="text-white/60">Loading…</div> : (
          <>
            <h2 className="font-display text-2xl uppercase font-black leading-none mb-1">
              {KIND_LABELS[data.transaction.kind] || data.transaction.kind}
            </h2>
            <div className="text-xs text-white/50 font-mono mb-6">{data.transaction.phonepe_merchant_order_id}</div>

            <div className="grid grid-cols-2 gap-3 text-xs mb-6">
              <KV k="Amount" v={`₹${Number(data.transaction.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`} />
              <KV k="Status" v={<StatusBadge s={data.transaction.payment_status} />} />
              <KV k="Payer" v={data.transaction.payer_name || "—"} />
              <KV k="Contact" v={data.transaction.payer_email || data.transaction.payer_phone || "—"} />
              <KV k="Created" v={fmtDate(data.transaction.created_at)} />
              <KV k="Paid at" v={data.transaction.paid_at ? fmtDate(data.transaction.paid_at) : "—"} />
              <KV k="Method" v={(data.transaction.method || "upi").toUpperCase()} />
              <KV k="Provider Ref" v={data.transaction.razorpay_payment_id || "—"} />
            </div>

            {data.transaction.description && (
              <div className="mb-6 text-sm text-white/80">{data.transaction.description}</div>
            )}

            <div className="flex gap-2 mb-6">
              <button onClick={replay} disabled={replaying || data.transaction.payment_status === "paid"} data-testid="pp-drawer-replay"
                      className="border border-brand-orange/50 brand-orange px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-brand-orange/10 disabled:opacity-40">
                {replaying ? "Replaying…" : "Re-poll & Reconcile"}
              </button>
            </div>

            <h3 className="font-display text-sm uppercase font-bold mb-2 brand-orange">Webhook Events ({data.events.length})</h3>
            <div className="border border-white/10 mb-6 max-h-72 overflow-y-auto">
              {data.events.length === 0 && <div className="text-white/40 p-4 text-xs">No webhook events yet.</div>}
              {data.events.map((e, i) => (
                <div key={i} className="border-b border-white/5 last:border-b-0 p-3 text-xs font-mono">
                  <div className="flex justify-between text-white/50 mb-1">
                    <span>{e.event}</span>
                    <span>{fmtDate(e.received_at)}</span>
                  </div>
                  <div className="text-white/70">state = <span className="brand-blue">{e.state || "—"}</span></div>
                </div>
              ))}
            </div>

            <h3 className="font-display text-sm uppercase font-bold mb-2 brand-orange">Refunds ({data.refunds.length})</h3>
            <div className="border border-white/10">
              {data.refunds.length === 0 && <div className="text-white/40 p-4 text-xs">No refunds against this order.</div>}
              {data.refunds.map((r, i) => (
                <div key={i} className="border-b border-white/5 last:border-b-0 p-3 text-xs">
                  <div className="flex justify-between font-mono text-white/60 mb-1">
                    <span>{r.merchant_refund_id}</span>
                    <span>{fmtDate(r.created_at)}</span>
                  </div>
                  <div>₹{Number((r.amount_paise || 0) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })} · {r.state || "queued"}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------- Refunds ----------------
function RefundsTable() {
  const [rows, setRows] = useState([]); const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1); const [pages, setPages] = useState(1);
  const [state, setState] = useState("");
  useEffect(() => { (async () => {
    try {
      const params = new URLSearchParams({ page: String(page), limit: "25" });
      if (state) params.set("state", state);
      const { data } = await api.get(`/admin/phonepe/report/refunds?${params.toString()}`);
      setRows(data.rows); setTotal(data.total); setPages(data.pages);
    } catch (e) { toast.error(apiError(e)); }
  })(); }, [page, state]);
  return (
    <div>
      <div className="flex gap-2 mb-3">
        <FilterSelect value={state} onChange={setState}
                      options={[["", "All states"], ["COMPLETED", "Completed"], ["PENDING", "Pending"], ["FAILED", "Failed"]]} testid="pp-refund-state" />
      </div>
      <div className="border border-white/10 overflow-x-auto" data-testid="pp-report-refunds-table">
        <table className="w-full text-xs">
          <thead className="text-white/50 font-mono uppercase tracking-widest text-[10px] border-b border-white/10">
            <tr><Th>When</Th><Th>Refund ID</Th><Th>Order ID</Th><Th>Amount</Th><Th>State</Th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><Td colSpan={5} className="text-center text-white/40 py-8">No refunds yet.</Td></tr>}
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-white/5">
                <Td className="text-white/60 whitespace-nowrap">{fmtDate(r.created_at)}</Td>
                <Td className="font-mono text-[11px] break-all">{r.merchant_refund_id}</Td>
                <Td className="font-mono text-[11px] break-all">{r.original_order_id}</Td>
                <Td>₹{Number((r.amount_paise || 0) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</Td>
                <Td>{r.state || <span className="text-white/40">queued</span>}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager page={page} pages={pages} total={total} onPage={setPage} />
    </div>
  );
}

// ---------------- Webhooks ----------------
function WebhooksTable() {
  const [rows, setRows] = useState([]); const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1); const [pages, setPages] = useState(1);
  const [event, setEvent] = useState(""); const [orderId, setOrderId] = useState("");
  const [preview, setPreview] = useState(null);

  const load = async () => {
    try {
      const params = new URLSearchParams({ page: String(page), limit: "50" });
      if (event) params.set("event", event);
      if (orderId) params.set("order_id", orderId);
      const { data } = await api.get(`/admin/phonepe/report/webhooks?${params.toString()}`);
      setRows(data.rows); setTotal(data.total); setPages(data.pages);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [page, event]);
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        <SearchBox value={orderId} onSearch={(v) => { setOrderId(v); setPage(1); setTimeout(load, 0); }} placeholder="Filter by order id" />
        <FilterSelect value={event} onChange={(v) => { setEvent(v); setPage(1); }}
          options={[
            ["", "All events"],
            ["checkout.order.completed", "checkout.order.completed"],
            ["checkout.order.failed",    "checkout.order.failed"],
            ["pg.refund.completed",       "pg.refund.completed"],
            ["pg.refund.failed",          "pg.refund.failed"],
          ]} testid="pp-webhook-event" />
      </div>
      <div className="border border-white/10 overflow-x-auto" data-testid="pp-report-webhooks-table">
        <table className="w-full text-xs">
          <thead className="text-white/50 font-mono uppercase tracking-widest text-[10px] border-b border-white/10">
            <tr><Th>Received</Th><Th>Event</Th><Th>Order ID</Th><Th>State</Th><Th></Th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><Td colSpan={5} className="text-center text-white/40 py-8">No webhook events.</Td></tr>}
            {rows.map((w, i) => (
              <tr key={i} className="border-b border-white/5 hover:bg-white/2">
                <Td className="text-white/60 whitespace-nowrap">{fmtDate(w.received_at)}</Td>
                <Td className="font-mono text-[11px]">{w.event}</Td>
                <Td className="font-mono text-[11px] break-all">{w.order_id || "—"}</Td>
                <Td>{w.state || "—"}</Td>
                <Td>
                  <button onClick={() => setPreview(w)} className="text-brand-blue hover:underline font-mono text-[10px] uppercase tracking-widest">Payload</button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager page={page} pages={pages} total={total} onPage={setPage} />
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" data-testid="pp-webhook-payload">
          <div className="absolute inset-0 bg-black/70" onClick={() => setPreview(null)} />
          <div className="relative bg-[#0F1524] border border-white/12 max-w-3xl w-full max-h-[80vh] overflow-y-auto p-6">
            <button onClick={() => setPreview(null)} className="absolute top-3 right-3 text-white/60 hover:text-white"><X size={20} /></button>
            <div className="font-mono text-xs uppercase tracking-widest brand-orange mb-2">// Raw Payload</div>
            <pre className="text-[11px] font-mono text-white/80 whitespace-pre-wrap break-all">
              {JSON.stringify(preview.payload, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Reusable bits ----------------
function SummaryTile({ icon, label, value, sub, color }) {
  const colors = {
    green: "border-green-500/30 text-green-300",
    yellow: "border-yellow-500/30 text-yellow-300",
    red: "border-red-500/30 text-red-300",
    blue: "border-brand-blue/30 brand-blue",
    orange: "border-brand-orange/30 brand-orange",
  }[color] || "border-white/15 text-white";
  return (
    <div className={`bg-[#131A2B] border p-4 ${colors}`}>
      <div className="flex items-center gap-2 opacity-70 mb-1">
        {icon}
        <span className="text-[10px] font-mono uppercase tracking-widest">{label}</span>
      </div>
      <div className="font-display text-2xl font-black leading-none">{value}</div>
      {sub && <div className="text-[10px] font-mono opacity-60 mt-1">{sub}</div>}
    </div>
  );
}

function StatusBadge({ s }) {
  const cls = STATUS_COLORS[s] || "border-white/20 text-white/60 bg-white/5";
  return <span className={`inline-block px-2 py-0.5 border font-mono text-[10px] uppercase tracking-widest ${cls}`}>{s || "pending"}</span>;
}

function SearchBox({ value, onSearch, placeholder }) {
  const [v, setV] = useState(value || "");
  return (
    <div className="relative">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
      <input value={v} onChange={(e) => setV(e.target.value)}
             onKeyDown={(e) => e.key === "Enter" && onSearch(v)}
             placeholder={placeholder}
             data-testid="pp-report-search"
             className="pl-9 pr-3 py-2 bg-transparent border border-white/15 text-xs font-mono w-72" />
    </div>
  );
}

function FilterSelect({ value, onChange, options, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
            className="bg-[#0B111E] border border-white/15 text-xs font-mono px-2 py-2">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function Pager({ page, pages, total, onPage }) {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-3 text-xs font-mono text-white/50">
      <div>{total} results · page {page}/{pages}</div>
      <div className="flex gap-1">
        <button onClick={() => onPage(Math.max(1, page - 1))} disabled={page === 1}
                data-testid="pp-report-prev"
                className="border border-white/15 px-3 py-1 disabled:opacity-30">Prev</button>
        <button onClick={() => onPage(Math.min(pages, page + 1))} disabled={page === pages}
                data-testid="pp-report-next"
                className="border border-white/15 px-3 py-1 disabled:opacity-30">Next</button>
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="text-left px-3 py-2">{children}</th>;
const Td = ({ children, className = "", colSpan }) => <td colSpan={colSpan} className={`px-3 py-2 ${className}`}>{children}</td>;
const KV = ({ k, v }) => (
  <div className="border border-white/10 p-3">
    <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-1">{k}</div>
    <div className="text-sm">{v}</div>
  </div>
);

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return s; }
}
